import { cn } from '@utazzo/ui';
import {
  ChevronsRight,
  Crosshair,
  HeartHandshake,
  ImageIcon,
  Luggage,
  Plane,
  Star,
  TrendingUp,
  Users,
  type LucideIcon,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { HScroller } from './HScroller';
import { WelcomeIcon } from './WelcomeIcon';

import type { WelcomeSectionOf, WelcomeSectionType, WelcomeStar } from '@utazzo/shared';

/**
 * Presentational sections of the Welcome Utazzer page. Brand colours are the
 * design's fixed palette (the admin app has no dark theme to adapt to).
 */
export const BRAND = {
  teal: '#093F51',
  tealDeep: '#0B3A4B',
  cyan: '#E0F9FC',
  yellow: '#FFE600',
} as const;

type Media = Record<string, string>;
type S<T extends WelcomeSectionType> = WelcomeSectionOf<T>;

/** Section heading: Bold 39.04px, line-height 100% (smaller on narrow widths). */
export const HEADING_CLASS =
  'font-heading text-[26px] leading-none font-bold text-[#093F51] @xl:text-[32px] @3xl:text-[39.04px]';
/** Section description: Light 19.52px (smaller on narrow widths), comfortable reading line-height. */
export const DESCRIPTION_CLASS =
  'text-[16px] leading-[1.6] tracking-normal font-light text-[#1f2d33] @xl:text-[18px] @3xl:text-[19.52px]';

const url = (media: Media, id: string | null | undefined): string | undefined =>
  id ? media[id] : undefined;

const paragraphs = (text: string): string[] =>
  text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

export const SectionHeading = ({
  title,
  subtitle,
  className,
}: {
  title: string;
  subtitle?: string;
  className?: string;
}): React.JSX.Element | null => {
  if (!title && !subtitle) return null;
  return (
    <div className={cn('mb-5 @xl:mb-6', className)}>
      {title && <h2 className={HEADING_CLASS}>{title}</h2>}
      {subtitle && <p className={cn('mt-3', DESCRIPTION_CLASS)}>{subtitle}</p>}
    </div>
  );
};

const Img = ({
  src,
  alt,
  className,
}: {
  src: string | undefined;
  alt: string;
  className?: string;
}): React.JSX.Element =>
  src ? (
    <img src={src} alt={alt} loading="lazy" className={cn('object-cover', className)} />
  ) : (
    <div
      className={cn('flex items-center justify-center bg-[#E0F9FC] text-[#093F51]/40', className)}
      aria-hidden
    >
      <ImageIcon className="h-8 w-8" />
    </div>
  );

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------

const PILLAR_ICONS: LucideIcon[] = [Crosshair, Users, TrendingUp, HeartHandshake, Star];

const Gauge = (): React.JSX.Element => (
  <svg viewBox="0 0 240 150" className="h-auto w-full max-w-[320px]" aria-hidden>
    <path
      d="M 30 130 A 90 90 0 0 1 210 130"
      fill="none"
      stroke={BRAND.yellow}
      strokeWidth="30"
      strokeDasharray="34 6"
    />
    <path
      d="M 58 130 A 62 62 0 0 1 182 130"
      fill="none"
      stroke={BRAND.yellow}
      strokeOpacity="0.35"
      strokeWidth="2"
    />
    <g transform="rotate(38 120 130)">
      <animateTransform
        attributeName="transform"
        type="rotate"
        from="-70 120 130"
        to="38 120 130"
        dur="1.6s"
        calcMode="spline"
        keyTimes="0;1"
        keySplines="0.34 1.2 0.64 1"
        fill="freeze"
      />
      <path d="M 116 130 L 120 52 L 124 130 Z" fill={BRAND.yellow} />
    </g>
    <circle cx="120" cy="130" r="11" fill={BRAND.yellow} />
    <circle cx="120" cy="130" r="4" fill={BRAND.tealDeep} />
  </svg>
);

/**
 * Hero. The designed poster is uploaded as the banner image and shown at its
 * own aspect ratio (never cropped); the text fallback below only renders when
 * no image is set.
 */
export const HeroSection = ({
  hero,
  media,
}: {
  hero: S<'hero'>;
  media: Media;
}): React.JSX.Element => {
  const desktop = url(media, hero.imageMediaId);
  const mobile = url(media, hero.mobileImageMediaId) ?? desktop;

  if (desktop || mobile) {
    return (
      <picture className="block">
        {mobile && <source media="(max-width: 639px)" srcSet={mobile} />}
        <img
          src={desktop ?? mobile}
          alt={`${hero.titleTop} ${hero.titleBottom}`}
          className="block h-auto w-full rounded-[13.55px]"
        />
      </picture>
    );
  }

  const words = hero.titleBottom.trim().split(/\s+/);
  const accent = words.length > 1 ? words.pop() : undefined;

  return (
    <div className="overflow-hidden rounded-[13.55px] bg-[#0B3A4B] px-5 py-6 text-white @xl:px-8 @xl:py-8">
      <div className="grid items-center gap-6 @2xl:grid-cols-[1.35fr_1fr]">
        <div>
          <p className="text-sm font-semibold tracking-wide text-white/80">UTAZZO Holidays</p>
          <h1 className="font-heading mt-2 text-4xl leading-[0.95] font-black italic @xl:text-5xl @4xl:text-6xl">
            {hero.titleTop}
            <br />
            {words.join(' ')} {accent && <span className="text-[#FFE600]">{accent}</span>}
          </h1>
          <div className="mt-4 h-px w-full max-w-md bg-[#FFE600]/60" />
          <p className="mt-3 text-base font-semibold @xl:text-xl">
            {hero.tagline}{' '}
            {hero.taglineAccent && <span className="text-[#FFE600]">{hero.taglineAccent}</span>}
          </p>
        </div>
        <div className="flex justify-center @2xl:justify-end">
          <Gauge />
        </div>
      </div>

      {hero.pillars.length > 0 && (
        <ul className="mt-6 grid grid-cols-2 gap-x-3 gap-y-4 @xl:grid-cols-3 @4xl:grid-cols-5 @4xl:gap-0">
          {hero.pillars.map((p, i) => {
            const Icon = PILLAR_ICONS[i % PILLAR_ICONS.length] ?? Star;
            return (
              <li
                key={`${p.title}-${i}`}
                className="flex items-start gap-2 @4xl:border-l @4xl:border-white/25 @4xl:px-3 @4xl:first:border-l-0 @4xl:first:pl-0"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/70">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="min-w-0">
                  <span className="block text-[11px] font-bold tracking-wide text-[#FFE600] uppercase">
                    {p.title}
                  </span>
                  <span className="block text-[11px] leading-snug text-white/85">{p.body}</span>
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {(hero.footerLine || hero.footerAccent) && (
        <p className="mt-6 flex flex-wrap items-center justify-center gap-x-1.5 border-t border-white/25 pt-3 text-center text-xs font-bold tracking-wider uppercase @xl:text-sm">
          {hero.footerLine}
          {hero.footerAccent && (
            <span className="inline-flex items-center text-[#FFE600]">
              {hero.footerAccent}
              <ChevronsRight className="h-4 w-4" />
            </span>
          )}
        </p>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Story / vision / journey
// ---------------------------------------------------------------------------

export const TextSection = ({ story }: { story: S<'text'> }): React.JSX.Element => (
  <section>
    <SectionHeading title={story.heading} className="mb-3" />
    <div className={cn('space-y-4', DESCRIPTION_CLASS)}>
      {paragraphs(story.body).map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </div>
  </section>
);

/**
 * Two-column card (Vision / Mission). Design: 942.59 x 220.40 card, 31.68px
 * radius; titles Black 29.28px / 100%, text Light 19.52px. On narrow widths
 * the columns stack and the card grows with its content.
 */
export const TwoColumnSection = ({ vm }: { vm: S<'twoColumn'> }): React.JSX.Element => (
  <section className="mx-auto grid w-full max-w-[942.59px] items-center gap-5 rounded-[24px] bg-[#E0F9FC] px-5 py-6 shadow-[0_8px_24px_-12px_rgba(9,63,81,0.35)] @xl:rounded-[31.68px] @xl:px-8 @2xl:min-h-[220.4px] @2xl:grid-cols-2 @2xl:gap-0 @2xl:py-8">
    {[
      { title: vm.leftTitle, body: vm.left },
      { title: vm.rightTitle, body: vm.right },
    ].map((b, i) => (
      <div
        key={i}
        className={cn(
          'flex h-full flex-col justify-center text-center @2xl:px-8',
          i === 1 && 'border-t border-[#093F51]/25 pt-5 @2xl:border-t-0 @2xl:border-l @2xl:pt-0',
        )}
      >
        <h3 className="font-heading text-[22px] leading-none font-black text-[#093F51] @xl:text-[26px] @3xl:text-[29.28px]">
          {b.title}
        </h3>
        <p className={cn('mt-4', DESCRIPTION_CLASS)}>{b.body}</p>
      </div>
    ))}
  </section>
);

export const PhotosSection = ({
  journey,
  media,
}: {
  journey: S<'photos'>;
  media: Media;
}): React.JSX.Element => {
  const photos = journey.photoMediaIds.map((id) => url(media, id)).filter(Boolean) as string[];
  return (
    <section>
      <SectionHeading title={journey.heading} className="mb-2" />
      {journey.body && <p className={DESCRIPTION_CLASS}>{journey.body}</p>}
      {photos.length > 0 && (
        <HScroller label="photos" className="mt-4" alwaysShowArrows>
          {photos.map((src, i) => (
            // Design card shape 278.14 x 314.74, 30.06px radius. Exactly 3 per view on
            // wide screens, 2 on tablets, 1 (with a peek of the next) on phones. Gaps
            // are px because the admin's root font-size is 17px.
            <div
              key={i}
              className="w-[78%] shrink-0 snap-start @md:w-[calc((100%-12px)/2)] @xl:w-[calc((100%-16px)/2)] @2xl:w-[calc((100%-32px)/3)]"
            >
              <Img src={src} alt="" className="aspect-[278.14/314.74] w-full rounded-[30.06px]" />
            </div>
          ))}
        </HScroller>
      )}
      {journey.ctaLabel && journey.ctaHref && (
        <div className="mt-6 flex justify-center">
          <a
            href={journey.ctaHref}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-xl bg-[#FFE600] px-5 py-2 font-bold text-[#093F51] underline underline-offset-2 transition hover:brightness-95"
          >
            {/instagram\.com/i.test(journey.ctaHref) && (
              <WelcomeIcon name="instagram" className="h-6 w-6" />
            )}
            {journey.ctaLabel}
          </a>
        </div>
      )}
    </section>
  );
};

// ---------------------------------------------------------------------------
// Founders
// ---------------------------------------------------------------------------

export const PeopleSection = ({
  founders,
  media,
}: {
  founders: S<'people'>;
  media: Media;
}): React.JSX.Element => (
  <section>
    <SectionHeading title={founders.heading} subtitle={founders.subheading} />
    <div className="space-y-10 @xl:space-y-12">
      {founders.items.map((f, i) => (
        // Design: 946 x 262 card (34.62px radius); 187 x 181.96 photo (13.39px
        // radius) sitting 97px above the card, 46px in from its left edge. Name +
        // role sit beside the photo, the quote runs full width below it. Below
        // @xl the photo shrinks and the name drops under it.
        <article
          key={`${f.name}-${i}`}
          className="relative mx-auto w-full max-w-[946px] pt-[60px] @xl:pt-[97px]"
        >
          <Img
            src={url(media, f.photoMediaId)}
            alt={f.name}
            className="absolute top-0 left-5 z-10 aspect-[187/181.96] w-[120px] rounded-[13.39px] shadow-md @xl:left-[46px] @xl:w-[187px]"
          />
          <div className="rounded-[24px] bg-gradient-to-r from-[#D5F7FB] to-white px-5 pb-6 shadow-[0_8px_24px_-14px_rgba(9,63,81,0.35)] @xl:min-h-[262px] @xl:rounded-[34.62px] @xl:px-10 @xl:pb-8">
            <div className="pt-[70px] @xl:min-h-[85px] @xl:pt-6 @xl:pl-[222px]">
              <h3 className="font-heading text-lg font-bold text-[#093F51] @xl:text-xl">
                {f.name}
              </h3>
              {f.role && <p className="text-sm text-[#093F51] @xl:text-base">{f.role}</p>}
            </div>
            {f.quote && (
              <p className={cn('mt-4 italic @xl:mt-5', DESCRIPTION_CLASS)}>
                “{f.quote.replace(/^[“"]|[”"]$/g, '')}”
              </p>
            )}
          </div>
        </article>
      ))}
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// Campaigns ("What we're putting out there")
// ---------------------------------------------------------------------------

const PAGE = 3;

const formatDate = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const CampaignsSection = ({
  campaigns,
  media,
}: {
  campaigns: S<'campaigns'>;
  media: Media;
}): React.JSX.Element => {
  const [filter, setFilter] = useState<string | null>(null);
  const [shown, setShown] = useState(PAGE);

  const items = useMemo(
    () =>
      filter
        ? campaigns.items.filter((c) => c.destination.toLowerCase() === filter.toLowerCase())
        : campaigns.items,
    [campaigns.items, filter],
  );
  const visible = items.slice(0, shown);
  const hasMore = items.length > shown;

  return (
    <section>
      <SectionHeading title={campaigns.heading} subtitle={campaigns.subheading} />

      {campaigns.destinations.length > 0 && (
        // The row fills the width between the arrows, and each circle takes an
        // equal share of it: 10 in view on a wide page, stepping down to 8, 6,
        // 4 and 3 as it narrows, so there is never an empty strip at the end.
        // One fixed gap (`@xl:gap-3` overrides the scroller's wider one) so
        // the share maths below holds at every width.
        <HScroller
          label="destinations"
          arrows="side"
          // Auto margins on the ends centre a short list (5 destinations do not
          // leave a blank strip on the right) and collapse to 0 once it scrolls.
          viewportClassName="@xl:gap-3 [&>*:first-child]:ms-auto [&>*:last-child]:me-auto"
        >
          {campaigns.destinations.map((d, i) => {
            const active = filter?.toLowerCase() === d.name.toLowerCase();
            const src = url(media, d.imageMediaId);
            return (
              <button
                key={`${d.name}-${i}`}
                type="button"
                onClick={() => {
                  setFilter(active ? null : d.name);
                  setShown(PAGE);
                }}
                aria-pressed={active}
                className="flex min-w-0 shrink-0 basis-[calc((100%_-_2*0.75rem)/3)] snap-start flex-col items-center gap-1.5 @lg:basis-[calc((100%_-_3*0.75rem)/4)] @2xl:basis-[calc((100%_-_5*0.75rem)/6)] @4xl:basis-[calc((100%_-_7*0.75rem)/8)] @5xl:basis-[calc((100%_-_9*0.75rem)/10)]"
              >
                <span
                  className={cn(
                    // Square, and as wide as its share of the row up to the design's
                    // 83.34px - so on a narrow phone it shrinks instead of spilling.
                    'flex aspect-square w-full max-w-[83.34px] items-center justify-center overflow-hidden rounded-full bg-[#D5F7FB] text-lg font-bold text-[#093F51] ring-2 ring-transparent transition',
                    active && 'ring-[#093F51]',
                  )}
                >
                  {src ? (
                    <img src={src} alt="" className="h-full w-full object-cover" loading="lazy" />
                  ) : (
                    d.name.charAt(0)
                  )}
                </span>
                <span
                  className={cn(
                    'max-w-full truncate text-[11px] font-semibold text-[#093F51]',
                    active && 'underline underline-offset-2',
                  )}
                >
                  {d.name}
                </span>
              </button>
            );
          })}
        </HScroller>
      )}

      {campaigns.items.length > 0 && (
        <>
          {visible.length === 0 ? (
            <p className="mt-6 rounded-xl bg-white p-6 text-center text-sm text-[#093F51]/70">
              No creatives running for {filter} yet.
            </p>
          ) : (
            <div className="mt-6 grid grid-cols-1 gap-5 @xl:grid-cols-2 @4xl:grid-cols-3">
              {visible.map((c, i) => {
                const started = formatDate(c.startedOn);
                const card = (
                  <>
                    {started && (
                      <p className="text-sm text-[#1f2d33]">Started running on {started}</p>
                    )}
                    {c.platforms.length > 0 && (
                      <p className="mb-2 flex items-center gap-1.5 text-sm text-[#1f2d33]">
                        Platforms
                        {c.platforms.map((p) => (
                          <WelcomeIcon key={p} name={p} className="h-4 w-4 text-[#093F51]" />
                        ))}
                      </p>
                    )}
                    <Img
                      src={url(media, c.imageMediaId)}
                      alt={c.destination}
                      className="aspect-[4/5] w-full rounded-xl"
                    />
                  </>
                );
                return c.href ? (
                  <a
                    key={i}
                    href={c.href}
                    target="_blank"
                    rel="noreferrer"
                    className="block transition hover:-translate-y-0.5"
                  >
                    {card}
                  </a>
                ) : (
                  <div key={i}>{card}</div>
                );
              })}
            </div>
          )}
        </>
      )}

      {(hasMore || campaigns.viewMoreHref) && campaigns.viewMoreLabel && (
        <div className="mt-6 flex justify-center">
          {hasMore ? (
            <button
              type="button"
              onClick={() => setShown((n) => n + PAGE * 2)}
              className="w-full max-w-[180px] rounded-xl bg-[#FFE600] px-6 py-2 font-bold text-[#093F51] transition hover:brightness-95"
            >
              {campaigns.viewMoreLabel}
            </button>
          ) : (
            <a
              href={campaigns.viewMoreHref}
              target="_blank"
              rel="noreferrer"
              className="w-full max-w-[180px] rounded-xl bg-[#FFE600] px-6 py-2 text-center font-bold text-[#093F51] transition hover:brightness-95"
            >
              {campaigns.viewMoreLabel}
            </a>
          )}
        </div>
      )}
    </section>
  );
};

// ---------------------------------------------------------------------------
// Stats + values
// ---------------------------------------------------------------------------

export const StatsSection = ({ stats }: { stats: S<'stats'> }): React.JSX.Element => (
  <section>
    <SectionHeading title={stats.heading} />
    <ul className="grid grid-cols-2 gap-y-5 rounded-xl bg-white p-4 shadow-[0_6px_20px_-10px_rgba(9,63,81,0.4)] @md:grid-cols-4 @4xl:grid-cols-8">
      {stats.items.map((s, i) => (
        <li
          key={i}
          className="group flex cursor-default flex-col items-center px-1 text-center transition-transform duration-300 hover:-translate-y-1 active:-translate-y-1"
        >
          <span className="mb-1.5 flex h-12 w-12 items-center justify-center rounded-full bg-[#D5F7FB] text-[#093F51] transition-colors duration-300 group-hover:bg-[#093F51] group-hover:text-white group-active:bg-[#093F51] group-active:text-white">
            <WelcomeIcon name={s.icon} className="h-6 w-6" />
          </span>
          <span className="text-xs leading-tight font-bold text-[#1f2d33]">{s.value}</span>
          {s.label && (
            <span className="text-[10px] leading-tight text-[#1f2d33]/70">({s.label})</span>
          )}
        </li>
      ))}
    </ul>
  </section>
);

/**
 * Icon cards. Full content width so the grid lines up with the stats strip
 * above (3 across on wide screens, min height 281.70), icon / uploaded logo
 * box 62.36 x 59.46. Content is top-aligned so icons and titles line up
 * across a row.
 */
export const CardsSection = ({
  values,
  media,
}: {
  values: S<'cards'>;
  media: Media;
}): React.JSX.Element => (
  <section>
    <SectionHeading title={values.heading} />
    <div className="grid w-full grid-cols-1 gap-[16px] @md:grid-cols-2 @2xl:grid-cols-3">
      {values.items.map((v, i) => {
        const iconSrc = url(media, v.iconMediaId);
        return (
          <article
            key={i}
            className="flex flex-col items-center justify-start rounded-2xl bg-[#E0F9FC] px-5 pt-10 pb-8 text-center @md:min-h-[281.7px]"
          >
            <span className="flex h-[59.46px] w-[62.36px] shrink-0 items-center justify-center text-[#093F51]">
              {iconSrc ? (
                <img src={iconSrc} alt="" loading="lazy" className="h-full w-full object-contain" />
              ) : (
                <WelcomeIcon name={v.icon} className="h-full w-full" strokeWidth={1.4} />
              )}
            </span>
            <h3 className="font-heading mt-2 text-lg font-bold text-[#093F51]">{v.title}</h3>
            {v.subtitle && <p className="text-sm text-[#1f2d33]">{v.subtitle}</p>}
            {v.tagline && (
              <p className="mt-4 text-xs font-medium tracking-wider text-[#1f2d33] italic">
                {v.tagline}
              </p>
            )}
          </article>
        );
      })}
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// Stars
// ---------------------------------------------------------------------------

export const StarsSection = ({
  stars,
  items,
  media,
}: {
  stars: S<'stars'>;
  items: WelcomeStar[];
  media: Media;
}): React.JSX.Element => (
  <section>
    <SectionHeading title={stars.heading} className="mb-2" />
    {stars.body && <p className={cn('max-w-3xl', DESCRIPTION_CLASS)}>{stars.body}</p>}
    {items.length > 0 && (
      <HScroller label="stars" className="mt-3">
        {items.map((s, i) => {
          const src = url(media, s.photoMediaId);
          return (
            <article
              key={`${s.name}-${i}`}
              style={{ backgroundColor: s.bgColour }}
              className="relative flex h-36 w-[85%] shrink-0 snap-start overflow-hidden rounded-2xl text-white @xl:w-[calc((100%-2rem)/3)]"
            >
              <div className="relative z-10 w-[58%] p-3">
                <h3 className="font-heading text-sm leading-tight font-bold @xl:text-base">
                  {s.name}
                </h3>
                {s.blurb && (
                  <p className="mt-1.5 line-clamp-5 text-[10px] leading-snug text-white/90">
                    {s.blurb}
                  </p>
                )}
              </div>
              <span className="absolute -right-6 -bottom-8 h-32 w-32 rounded-full bg-white/20" />
              {src && (
                <img
                  src={src}
                  alt={s.name}
                  loading="lazy"
                  className="absolute right-0 bottom-0 h-full w-[45%] object-cover object-top"
                />
              )}
            </article>
          );
        })}
      </HScroller>
    )}
  </section>
);

// ---------------------------------------------------------------------------
// Expectations
// ---------------------------------------------------------------------------

const BORDERS = ['border-[#FFD500]', 'border-[#093F51]', 'border-[#3AA7B9]'];

const LinesList = ({ section }: { section: S<'list'> }): React.JSX.Element => (
  <section>
    <SectionHeading title={section.heading} />
    <div className="grid gap-x-8 gap-y-6 @2xl:grid-cols-2">
      {section.items.map((it, i) => (
        <div key={i} className={cn('border-l-2 pl-3', BORDERS[i % BORDERS.length])}>
          <h3 className="font-heading text-base font-bold text-[#093F51] @xl:text-lg">
            {it.title}
          </h3>
          {it.body && <p className="mt-1 text-sm leading-relaxed text-[#1f2d33]">{it.body}</p>}
        </div>
      ))}
    </div>
  </section>
);

const NumberedList = ({ section }: { section: S<'list'> }): React.JSX.Element => (
  <section>
    <SectionHeading title={section.heading} />
    <ol className="grid gap-4 @2xl:grid-cols-2 @2xl:gap-x-8 @2xl:gap-y-5">
      {section.items.map((it, i) => (
        <li
          key={i}
          className="flex gap-3 rounded-xl border border-l-4 border-[#093F51]/10 border-l-[#093F51] bg-white p-3 shadow-[0_4px_14px_-8px_rgba(9,63,81,0.45)]"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#093F51] text-sm font-bold text-white">
            {String(i + 1).padStart(2, '0')}
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-[#093F51]">{it.title}</h3>
            {it.body && <p className="mt-0.5 text-xs leading-relaxed text-[#1f2d33]">{it.body}</p>}
          </div>
        </li>
      ))}
    </ol>
  </section>
);

/** Bullet-style (Employee Expectations) or numbered cards (Employer Expectations). */
export const ListSection = ({ section }: { section: S<'list'> }): React.JSX.Element =>
  section.style === 'numbered' ? (
    <NumberedList section={section} />
  ) : (
    <LinesList section={section} />
  );

// ---------------------------------------------------------------------------
// Closing banner
// ---------------------------------------------------------------------------

export const BannerSection = ({
  closing,
  media,
}: {
  closing: S<'banner'>;
  media: Media;
}): React.JSX.Element => {
  const src = url(media, closing.imageMediaId);
  return (
    <section className="relative overflow-hidden rounded-xl bg-[#0B3A4B] px-5 py-5 text-center text-white @xl:px-8">
      <Plane className="absolute top-4 right-4 h-6 w-6 rotate-45 fill-white @xl:h-7 @xl:w-7" />
      <div className="flex flex-col items-center gap-3 @xl:flex-row @xl:gap-5">
        {src ? (
          <img src={src} alt="" className="h-16 w-16 shrink-0 object-contain @xl:h-20 @xl:w-20" />
        ) : (
          <Luggage
            className="h-12 w-12 shrink-0 text-[#C27C3A] @xl:h-16 @xl:w-16"
            strokeWidth={1.5}
          />
        )}
        <div className="min-w-0 flex-1">
          {closing.eyebrow && <p className="text-sm @xl:text-lg">{closing.eyebrow}</p>}
          <p className="font-heading text-2xl leading-tight font-black text-[#FFE600] @xl:text-4xl">
            {closing.title}
          </p>
          {closing.pillars.length > 0 && (
            <p className="mt-2 flex flex-wrap justify-center gap-x-2 gap-y-1 text-xs text-[#FFE600] @xl:text-sm">
              {closing.pillars.map((p, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="text-white/60">|</span>}
                  <span>{p}</span>
                </React.Fragment>
              ))}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};
